import React from "react";
import { Box } from "@mui/material";
import { X } from "lucide-react";

import { Button } from "../Button";

/**
 * A toolbar control with a CLEAR affordance welded to its right edge.
 *
 * ## Why this is in `@repo/ui` and not in either app
 *
 * Because it had already been built twice and was about to be built a third
 * time. `apps/platform` builds it inline in BOTH `input/Sort/Sort.tsx` and
 * `input/filters/Filters.tsx`; `apps/app` built it again inside
 * `components/UI/ToolbarControl`. That file's own header has carried the note
 * since it was written — *"the split-button geometry now exists TWICE in the
 * monorepo … the right home is `packages/ui`, so both apps consume one
 * segmented-control primitive … it is the durable follow-up"* — and adding a
 * clear affordance to the app's Filter control is exactly the third copy that
 * note was predicting. So the geometry moves down a layer instead
 * (working-conventions §47: improve layer 1 rather than rebuild sideways).
 *
 * ## What it owns, and what it deliberately does not
 *
 * It owns the SEAM and the MOTION — the two things that are fiddly, invisible
 * when wrong, and identical everywhere:
 *
 * - the 1px border overlap that makes the join read as a single hairline rather
 *   than two adjacent borders,
 * - squaring only the corners that actually touch,
 * - the clear segment sliding out from *under* the main button and back,
 * - keeping the row's geometry byte-identical while parked.
 *
 * It owns NO surface styling: fill, border, radius, height and label typography
 * all arrive as `controlSx` from the consumer, because those are per-app bands
 * (`apps/app` sits its toolbar at 44/48 against a 48px field; the platform sits
 * its at 49 against a 49px one) and baking either in would force one app to
 * override the other's, which is the divergence this exists to end.
 */

/** Width of the clear segment. Matches the platform's measured 48. */
const CLEAR_SEGMENT_WIDTH = 48;

/**
 * How the clear segment gets on and off screen: it SLIDES OUT from under the
 * main button's right edge, and slides back under it when cleared.
 *
 * ## Why a translate under the button, and not a width animation or a fade
 *
 * A fade was rejected outright — the segment is physically welded to the
 * control, so appearing *from* it is the honest description of what happens,
 * and a fade says nothing about where it came from.
 *
 * The obvious mechanic is a wrapper with `overflow: hidden` animating 0 -> 48.
 * It was built and discarded: at rest that wrapper is exactly the button's own
 * size, so it permanently clips the Button's hover shadow AND — the
 * disqualifying one — the UA focus ring, which is drawn outside the border box.
 * Clipping a keyboard user's focus indicator to buy an animation is not a trade
 * worth making.
 *
 * So there is no clipping anywhere. Instead:
 *
 * - **`transform: translateX(-48px)`** parks the segment directly behind the
 *   main button, which is opaque and given `zIndex: 1` against the segment's
 *   `0`. The segment is hidden by being *behind something*, not by being cut off.
 * - **`marginRight: -47px`** cancels its layout width while parked
 *   (`-1` left margin `+ 48` width `- 47` = 0), so a toolbar with nothing to
 *   clear is byte-identical in geometry to one that never had the segment.
 * - Animating BOTH together is what stops the row jolting: the 48px the
 *   neighbours must give up is handed over across the same 220ms the segment
 *   takes to emerge, so the next control slides rather than jumps.
 */
const CLEAR_SLIDE_MS = 220;

/**
 * The app's page-slide curve. Heavily front-loaded, which is right for a glide.
 * Deliberately NOT an overshoot: overshooting a width would push the whole
 * toolbar past its final layout and pull it back, which is the jitter this is
 * meant to avoid.
 */
const CLEAR_SLIDE_EASING = "cubic-bezier(0.32, 0.72, 0, 1)";

export type SplitControlProps = {
  /** The main button's contents — icon, label, count pill. */
  children: React.ReactNode;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /** Accessible name for the main button. */
  label: string;
  /**
   * The shared surface: fill, border, radius, height, padding. Supplied by the
   * consumer because the band is per-app — see the note above.
   */
  controlSx: any;
  /** Corner radius in px, so the seam can square only the touching corners. */
  radius: number;
  /**
   * Clearing the control. Omit `onClear` entirely and no segment is rendered or
   * mounted; supply it and the segment mounts but stays parked until
   * `showClear`.
   */
  onClear?: () => void;
  showClear?: boolean;
  /** Accessible name for the clear half, e.g. "Clear sort". */
  clearLabel?: string;
  /** Forwarded to the main button, for anchoring a menu against it. */
  buttonRef?: React.Ref<HTMLButtonElement>;
  disabled?: boolean;
  /**
   * Hide the clear segment below this breakpoint. On a phone the sheet these
   * controls open carries its own Clear, and a fourth control does not fit the
   * row — so the segment is `display: none` there, which also makes the whole
   * slide inert (a `display: none` element contributes no layout, so its
   * margins and transform never apply).
   */
  clearFromBreakpoint?: "xs" | "sm" | "md" | "lg";
};

export const SplitControl: React.FC<SplitControlProps> = ({
  children,
  onClick,
  label,
  controlSx,
  radius,
  onClear,
  showClear = false,
  clearLabel = "Clear",
  buttonRef,
  disabled,
  clearFromBreakpoint = "md",
}) => {
  /**
   * Is the clear segment OUT, as opposed to parked behind the main button?
   *
   * Distinct from whether it is RENDERED: it is mounted whenever the consumer
   * can clear at all, because an element that unmounts cannot slide away.
   */
  const clearable = Boolean(showClear && onClear);

  /**
   * ⚠️ RESPONSIVE, and it has to be: the segment is hidden below the
   * breakpoint, so squaring this edge unconditionally welds the main button to
   * thin air on a phone. The breakpoint belongs in CSS, next to the `display`
   * it must agree with — a JS media query cannot be trusted here because
   * `useMediaQuery` does not re-evaluate on a live resize in `apps/app`.
   */
  const splitLeftSx = {
    ...controlSx,
    borderRadius: {
      xs: `${radius}px !important`,
      [clearFromBreakpoint]: `${radius}px 0 0 ${radius}px !important`,
    },
  };

  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", flexShrink: 0 }}>
      <Button
        ref={buttonRef}
        variant="tertiary"
        size="large"
        onClick={onClick}
        disabled={disabled}
        aria-label={label}
        sx={{
          // Square inner corners ONLY while something is welded to that edge.
          // With nothing to clear this is an ordinary standalone control and
          // must keep all four corners.
          ...(clearable ? splitLeftSx : controlSx),
          // Paints ABOVE the parked segment, which is what hides it. Both
          // halves have to be positioned for z-index to apply at all.
          position: "relative",
          zIndex: 1,
          // The corner change is instant in both directions by default, which is
          // wrong on the way OUT: rounding this edge the moment it is cleared
          // would seal the control while the segment is still sliding back
          // through it. A zero-duration transition with a delay defers the flip
          // to the end of the slide. Opening keeps delay 0 — the edge must be
          // square before anything emerges from it.
          transition: `all 0.2s ease-in, border-radius 0s ${
            clearable ? "0s" : `${CLEAR_SLIDE_MS}ms`
          }`,
          "@media (prefers-reduced-motion: reduce)": { transition: "none" },
        }}
      >
        {children}
      </Button>

      {/*
        Icon-only at every width, deliberately: it is the clear affordance
        ATTACHED to the control, not a peer of it, so the glyph is the whole
        message (working-conventions §38 — `X` clears a set rather than
        destroying a record). Labelling it would read as another control.

        lucide `X`, not MUI's `CloseIcon`: the MUI glyph is a FILLED path with no
        stroke, so it renders as a heavy black cross beside a row of 1px-stroke
        outline icons — the loudest thing in the toolbar, on its least important
        control.
      */}
      {onClear && (
        <Button
          variant="tertiary"
          size="large"
          onClick={onClear}
          disabled={disabled}
          aria-label={clearLabel}
          sx={{
            ...controlSx,
            borderRadius: `0 ${radius}px ${radius}px 0 !important`,
            /*
             * `marginLeft: -1px` is what makes the seam a single 1px line rather
             * than two adjacent borders reading as a 2px rule. The platform
             * reaches the same result by dropping the left button's right border
             * and drawing an inset shadow, but that route is not available in
             * `apps/app`, whose `controlSx` declares `border` as an `!important`
             * shorthand (its index.css applies a rule at ID specificity) and
             * whose Button owns `boxShadow` on hover. Overlapping the borders
             * needs neither property, so the two rules stop competing.
             */
            marginLeft: "-1px",
            minWidth: CLEAR_SEGMENT_WIDTH,
            width: CLEAR_SEGMENT_WIDTH,
            padding: 0,
            paddingX: 0,
            // 20px — a step below the control's own glyph. A clear affordance
            // should not carry the same weight as the thing it clears; the
            // platform makes the same distinction. `flexShrink: 0` is
            // load-bearing rather than defensive: width/height alone still let
            // flexbox shrink an SVG.
            "& svg": { width: 20, height: 20, flexShrink: 0 },
            "&:hover": {
              ...(controlSx?.["&:hover"] ?? {}),
              // Reddens only on engagement, matching the platform's own clear
              // button. The line is the CONTROL'S WEIGHT, not whether the action
              // destroys anything: a bare clear ICON may redden on reach, a full
              // labelled `Reset` BUTTON may not.
              color: "error.main",
            },
            display: { xs: "none", [clearFromBreakpoint]: "inline-flex" },
            position: "relative",
            zIndex: 0,
            transform: clearable
              ? "translateX(0)"
              : `translateX(-${CLEAR_SEGMENT_WIDTH}px)`,
            // Parked, it must cost the row nothing: -1 + 48 - 47 = 0.
            marginRight: clearable ? 0 : `-${CLEAR_SEGMENT_WIDTH - 1}px`,
            /*
             * `visibility` does the accessibility work. A parked-but-present
             * button is still tabbable and still announced — a screen reader
             * would offer "Clear sort" on a list with no sort applied.
             * `visibility: hidden` removes it from both the tab order and the
             * accessibility tree with no `aria-hidden` / `tabIndex` bookkeeping,
             * and unlike `display: none` it keeps the margins holding the layout
             * steady.
             *
             * The subtlety is the EXIT: hiding it at the start of the slide would
             * make the thing vanish instead of slide. `visibility` is discrete,
             * so a `0s` transition with a DELAY defers the flip cleanly.
             */
            visibility: clearable ? "visible" : "hidden",
            transition: [
              `transform ${CLEAR_SLIDE_MS}ms ${CLEAR_SLIDE_EASING}`,
              `margin-right ${CLEAR_SLIDE_MS}ms ${CLEAR_SLIDE_EASING}`,
              `visibility 0s ${clearable ? "0s" : `${CLEAR_SLIDE_MS}ms`}`,
            ].join(", "),
            "@media (prefers-reduced-motion: reduce)": { transition: "none" },
          }}
        >
          <X size={20} strokeWidth={1} />
        </Button>
      )}
    </Box>
  );
};

export default SplitControl;
